import * as THREE from 'three';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Color } from 'three';
import { GROUND_DEFAULT_COLOR } from '../constants';
import Elevator from './Elevator';
import { useTargetContext } from '../context/TargetContext';
import { FloorType } from '../types/floor';

const Floor = ({
  floorHeight,
  boxSize,
  width,
  height,
  floorMap,
  setShowLandingPage,
}: {
  width: number;
  height: number;
  floorHeight: number;
  boxSize: number;
  floorMap: FloorType;
  setShowLandingPage: (b: boolean) => void;
}) => {
  const { target } = useTargetContext();

  const [refreshMaterial, setRefreshMaterial] = useState(false);
  const refFloorInstancedMesh = useRef<THREE.InstancedMesh>(null);
  const cubeTextureImage = useMemo(
    () => require('../assets/2px_texture.png'),
    []
  );
  const cubeGeometry = useMemo(
    () => new THREE.BoxGeometry(boxSize, floorHeight, boxSize),
    [boxSize, floorHeight]
  );
  const cubeMaterial = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        map: new THREE.TextureLoader().load(cubeTextureImage, (tex) => {
          tex.magFilter = THREE.NearestFilter;
          tex.anisotropy = 16;
        }),
      }),
    [cubeTextureImage, refreshMaterial] // eslint-disable-line
  );
  const cubeMesh = useMemo(
    () => new THREE.Mesh(cubeGeometry.clone(), cubeMaterial.clone()),
    [cubeGeometry, cubeMaterial]
  );

  useEffect(() => setRefreshMaterial(true), []);

  useEffect(() => {
    if (refFloorInstancedMesh.current) {
      target === 'dev' && console.log('Draw Floor()');

      if (refreshMaterial) setRefreshMaterial(false);

      let counter = 0;

      for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
          const id = counter++;
          const position = {
            x: -y * boxSize,
            y: -floorHeight * 1.75,
            z: x * boxSize,
          };

          cubeMesh.position.set(position.x, position.y, position.z);
          cubeMesh.updateMatrix();

          refFloorInstancedMesh.current.setColorAt(
            id,
            new Color(GROUND_DEFAULT_COLOR)
          );

          if (refFloorInstancedMesh.current.instanceColor)
            refFloorInstancedMesh.current.instanceColor.needsUpdate = true;

          refFloorInstancedMesh.current.setMatrixAt(id, cubeMesh.matrix);
        }
      }
      refFloorInstancedMesh.current.instanceMatrix.needsUpdate = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxSize, cubeMesh, floorHeight, height, width]);

  return (
    <>
      <instancedMesh
        ref={refFloorInstancedMesh}
        args={[cubeMesh.geometry, cubeMesh.material, width * height]}
        renderOrder={10}
      />
      <Elevator
        width={width}
        height={height}
        boxSize={boxSize}
        onClick={() => {
          setShowLandingPage(true);
        }}
      />
    </>
  );
};

export default Floor;
